'use client';

import './nav-tabs.scss';

import { useEffect, useRef } from 'react';

import { Link } from '../../atoms/link/link';

/**
 * Positions the active tab within the visible scroll area on mobile.
 * For now we're just applying this for all screen sizes if focusOnMobile is true.
 */
const anchorActiveTab = (container: HTMLDivElement, list: HTMLDivElement) => {
  // TODO: NavTabs is unmounted with each route change, so this can't persist the current scroll pos
  if (!container || !list?.childElementCount) {
    return;
  }

  // Find the active tab (this is fragile)
  const activeTab = list.querySelector<HTMLElement>('[aria-current="true"]');
  if (!activeTab) {
    return;
  }

  // This runs when mounted, if the last tab is visible, get out
  const lastTab = list.children[list.childElementCount - 1];
  const lastRect = lastTab.getBoundingClientRect();
  const isFullyVisible = lastRect.right <= window.innerWidth;
  if (isFullyVisible) {
    return;
  }

  const containerWidth = container.clientWidth;
  const maxScrollLeft = Math.max(0, container.scrollWidth - containerWidth);
  const tabOffsetLeft = activeTab.offsetLeft;
  const tabWidth = activeTab.offsetWidth;

  let targetLeft = tabOffsetLeft - (containerWidth - tabWidth) / 2;
  if (lastTab === activeTab) {
    targetLeft = tabOffsetLeft + tabWidth - containerWidth;
  }

  // eslint-disable-next-line no-param-reassign
  container.scrollLeft = Math.min(Math.max(targetLeft, 0), maxScrollLeft);
};

export type LinkPlaceholder = {
  id: string;
  title: string;
  path: string;
  /**
   * In almost all cases this should be automatically handled by the Link component.
   */
  isActive?: boolean;
};

export type NavTabsProps = {
  links: LinkPlaceholder[];
  focusOnMobile?: boolean;
};

// TODO: accessibility considerations we need to have in mind for these tertiary navs
export const NavTabs: React.FC<NavTabsProps> = ({
  links,
  focusOnMobile = true,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusOnMobile) {
      return;
    }

    const container = scrollContainerRef.current;
    const list = listRef.current;
    if (!container || !list) {
      return;
    }

    anchorActiveTab(container, list);
  }, []);

  if (!links || links.length === 0) {
    return null;
  }

  return (
    <nav className="NavTabs relative w-100 overflow-hidden mb-comfy-xl">
      <div
        ref={scrollContainerRef}
        className="NavTabs__scrollcontainer overflow-x-auto"
      >
        <div
          ref={listRef}
          className="NavTabs__list flex gap-comfy-md"
          role="tablist"
        >
          {links.map((link) => {
            return (
              <Link
                key={link.id}
                href={link.path}
                className="NavTabs__tab label-potent label-lg pr-snug-md pb-comfy-xs pl-snug-md flex-shrink-0"
                // This is a very rare override.
                // In almost all cases this should be automatically handled by the Link component.
                {...(link.isActive && { 'aria-current': 'true' })}
              >
                {link.title}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
